/**
 * buildTreatmentPdfModel — pure view-model for the on-screen preview of the
 * single-treatment PDF. Mirrors the block order / rules of the server template
 * (functions/treatmentPdfHtmlTemplate.js) so preview ≈ PDF:
 *
 *   letterhead → addressee → title → patient meta → chips → Q&A → recommendations
 *   → signature → footnote
 *
 * Shared blocks come from components/pdfExport/buildPaperModel.js.
 */

import { formatHebrewDate, formatTime } from "../../../utils/dateUtils";
import {
  trim,
  buildLetterheadModel,
  buildPatientRows,
  buildSignatureModel,
  resolveLicenseNumber,
  therapistFullName,
  patientFullName,
} from "../../pdfExport/buildPaperModel";
import { isFieldEmpty, fieldDisplayValue, sanitizeFieldHtml } from "./treatmentFieldValue";

export const defaultTitleFor = (typeName, dateStr) =>
  ["סיכום טיפול", typeName, dateStr].filter(Boolean).join(" – ");

export const buildTreatmentPdfModel = ({
  treatment,
  patient,
  clinic,
  therapist,
  typeName,
  tags,
  options,
}) => {
  const o = options;
  const dateStr = treatment?.treatmentDate ? formatHebrewDate(treatment.treatmentDate) : "";
  const timeStr = treatment?.treatmentDate ? formatTime(treatment.treatmentDate) : "";
  const generatedAt = formatHebrewDate(new Date());

  const exporterName = therapistFullName(therapist);
  const authorName =
    trim(treatment?.therapistName) ||
    (treatment?.therapistId === therapist?.uid ? exporterName : "");
  const licenseNumber = resolveLicenseNumber(therapist, clinic);

  const letterhead = buildLetterheadModel({
    clinic,
    header: o.header,
    authorName,
    licenseNumber,
    generatedAt,
  });

  const title = trim(o.title) || defaultTitleFor(typeName, dateStr);

  const patientRows = buildPatientRows({
    patientToggles: o.patient,
    patientName: patientFullName(patient, treatment?.patientName),
    patientDoc: patient,
    dateLabel: "תאריך הטיפול",
    dateValue: dateStr ? (timeStr ? `${dateStr}, ${timeStr}` : dateStr) : "",
    typeName,
    authorName,
  });

  const chips = [];
  if (o.content.tags) {
    (treatment?.tagIds || []).forEach((id) => {
      const tag = (tags || []).find((t) => t.id === id);
      if (tag?.name) chips.push({ text: tag.name });
    });
  }
  if (o.content.version && Number(treatment?.currentVersion) > 1) {
    chips.push({ text: `גרסה ${Number(treatment.currentVersion)}` });
  }

  const fields = Array.isArray(treatment?.fields) ? treatment.fields : [];
  const blocks = [];
  fields.forEach((field) => {
    if (!field || typeof field !== "object") return;
    const label = trim(String(field.label || ""));
    if (field.type === "section_header") {
      if (label) blocks.push({ kind: "section", id: field.id, label });
      return;
    }
    const empty = isFieldEmpty(field);
    if (empty && o.content.hideEmptyAnswers) return;
    blocks.push({
      kind: "qa",
      id: field.id,
      label,
      empty,
      value: empty ? null : fieldDisplayValue(field),
    });
  });
  if (fields.length === 0 && (treatment?.title || treatment?.content)) {
    blocks.push({
      kind: "qa",
      id: "legacy",
      label: trim(treatment.title),
      empty: !treatment.content,
      value: treatment.content ? { kind: "html", html: sanitizeFieldHtml(treatment.content) } : null,
    });
  }

  return {
    letterhead,
    addressee: trim(o.addressee),
    title,
    patientRows,
    chips,
    blocks,
    recommendations: trim(o.recommendations),
    signature: buildSignatureModel({
      therapist,
      clinic,
      signatureMode: o.signature,
      licenseNumber,
      generatedAt,
    }),
    footerNote: trim(o.footerNote),
    hasContent: blocks.some((b) => b.kind === "qa"),
  };
};
